import { inject } from '@angular/core';
import { JsonSchema7 } from '@jsonforms/core';
import { FilterEventsCommand } from 'src/app/boxwood-api';
import { FacilitiesService } from 'src/app/handweave-api';
import { AlertsApi } from 'src/app/hickory-api';
import { ApiConfig } from 'src/app/shared/api-config/api-config';
import { AccessControl } from 'src/app/shared/authentication/access-control';
import { totalOf } from 'src/app/shared/dashboard-grid';
import { AlertPriority, DashboardFilterValues, TimeRange, timeWindowOf } from '../../dashboard.state';
import { sentAlertsRequest } from '../../data/alerts';
import { ALL_INCIDENTS, injectIncidentCount, OPEN_INCIDENTS } from '../../data/incidents';

export type MetricId = 'openIncidents' | 'incidentsOpened' | 'alertsSent' | 'scheduledAlerts' | 'facilities';

export type MetricFilter = keyof DashboardFilterValues;

/** A translation key and its parameters */
export interface MetricText {
  key: string;
  params?: Record<string, string | number>;
}

export interface MetricReading {
  value: number;
  detail?: MetricText;
}

/** A metric's own settings, stored on the tile beside `metric`, such as Alerts Sent's priority */
export type MetricOwnSettings = Record<string, unknown>;

export type MetricLoader<S extends MetricOwnSettings = MetricOwnSettings> = (
  filters: Partial<DashboardFilterValues>,
  settings: S,
  abortSignal: AbortSignal,
) => Promise<MetricReading>;

export interface MetricSettingsDefinition<S extends MetricOwnSettings> {
  /** An object schema of the settings; it validates stored tiles and generates their form under the metric picker */
  schema: JsonSchema7;
  /** Where picking the metric starts, and what a stored tile lacks */
  defaults: S;
  /** Replaces the metric's `filters` */
  filtersFor(settings: S): readonly MetricFilter[];
  /** Replaces the metric's `label` on the tile */
  labelFor?(settings: S): string;
}

export interface Metric<S extends MetricOwnSettings = MetricOwnSettings> {
  label: string;
  /** What the number counts, for the settings picker */
  description: string;
  icon: string;
  /**
   * The filters the reading depends on with the default settings, which the filter bar shows for the tile; changing
   * any other doesn't reload it
   */
  filters: readonly MetricFilter[];
  /** All are required to see a tile showing the metric, or to pick it */
  permissions?: readonly string[];
  settings?: MetricSettingsDefinition<S>;
  /** Runs in an injection context */
  injectLoader: () => MetricLoader<S>;
}

/** Whether Alerts Sent follows the Priority filter or counts one priority whatever it says */
export type AlertsSentPriority = 'filter' | Exclude<AlertPriority, 'all'>;

/** A type alias, not an interface, so it fits `MetricOwnSettings` */
export type AlertsSentSettings = { priority: AlertsSentPriority };

const ALERTS_SENT_LABELS: Record<AlertsSentPriority, string> = {
  filter: 'Dashboard.Metric.AlertsSent',
  emergency: 'Dashboard.Metric.EmergencyAlertsSent',
  nonEmergency: 'Dashboard.Metric.NonEmergencyAlertsSent',
};

/** Keeps a metric's own settings typed where it is defined; `METRICS` holds every metric alike */
function metric<S extends MetricOwnSettings>(definition: Metric<S>): Metric {
  return definition as unknown as Metric;
}

const INCIDENT_PERMISSIONS = [AccessControl.IncidentPermissionIncidentEventView];

export const METRICS: Record<MetricId, Metric> = {
  openIncidents: {
    label: 'Dashboard.Metric.OpenIncidents',
    description: 'Dashboard.Metric.OpenIncidentsDescription',
    icon: 'assignment',
    filters: [],
    permissions: INCIDENT_PERMISSIONS,
    injectLoader: injectOpenIncidents,
  },
  incidentsOpened: {
    label: 'Dashboard.Metric.IncidentsOpened',
    description: 'Dashboard.Metric.IncidentsOpenedDescription',
    icon: 'add_task',
    filters: ['timeRange'],
    permissions: INCIDENT_PERMISSIONS,
    injectLoader: injectIncidentsOpened,
  },
  alertsSent: metric<AlertsSentSettings>({
    label: 'Dashboard.Metric.AlertsSent',
    description: 'Dashboard.Metric.AlertsSentDescription',
    icon: 'sms_failed',
    filters: ['timeRange', 'priority'],
    settings: {
      schema: {
        type: 'object',
        properties: {
          priority: {
            type: 'string',
            title: 'Dashboard.Metric.Priority',
            oneOf: [
              { const: 'filter', title: 'Dashboard.Metric.FollowPriorityFilter' },
              { const: 'emergency', title: 'Dashboard.Filter.Emergency' },
              { const: 'nonEmergency', title: 'Dashboard.Filter.NonEmergency' },
            ],
          },
        },
      },
      defaults: { priority: 'filter' },
      filtersFor: ({ priority }) => (priority === 'filter' ? ['timeRange', 'priority'] : ['timeRange']),
      labelFor: ({ priority }) => ALERTS_SENT_LABELS[priority],
    },
    injectLoader: injectAlertsSent,
  }),
  scheduledAlerts: {
    label: 'Dashboard.Metric.ScheduledAlerts',
    description: 'Dashboard.Metric.ScheduledAlertsDescription',
    icon: 'schedule_send',
    filters: [],
    injectLoader: injectScheduledAlerts,
  },
  facilities: {
    label: 'Dashboard.Metric.Facilities',
    description: 'Dashboard.Metric.FacilitiesDescription',
    icon: 'business',
    filters: [],
    injectLoader: injectFacilities,
  },
};

function injectOpenIncidents(): MetricLoader {
  const count = injectIncidentCount();

  return async (_filters, _settings, abortSignal) => {
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const [open, openedToday] = await Promise.all([
      count(OPEN_INCIDENTS, abortSignal),
      count({ ...OPEN_INCIDENTS, createdStartDate: startOfToday.toISOString() }, abortSignal),
    ]);
    return { value: open, detail: { key: 'Dashboard.Metric.OpenedToday', params: { count: openedToday } } };
  };
}

/** Every incident created in the range, open or since closed, against the period before it. */
function injectIncidentsOpened(): MetricLoader {
  const count = injectIncidentCount();
  const createdIn = (range: TimeRange, now: Date, periodsBack: number): FilterEventsCommand => {
    const { after, before } = timeWindowOf(range, now, periodsBack);
    return { ...ALL_INCIDENTS, createdStartDate: after.toISOString(), createdEndDate: before.toISOString() };
  };

  return ({ timeRange = '24h' }, _settings, abortSignal) => {
    const now = new Date();
    return compareWithPrevious((periodsBack) => count(createdIn(timeRange, now, periodsBack), abortSignal));
  };
}

/**
 * Every sender's alerts in the range; `/v3/user/alerts/sent` would count only the signed-in admin's. A fixed
 * priority setting ignores the Priority filter.
 */
function injectAlertsSent(): MetricLoader<AlertsSentSettings> {
  const alerts = inject(AlertsApi);

  return ({ timeRange = '24h', priority: filtered = 'all' }, { priority }, abortSignal) => {
    const now = new Date();
    const filters = { timeRange, priority: priority === 'filter' ? filtered : priority };
    return compareWithPrevious((periodsBack) =>
      totalOf(
        alerts.findAlerts({ ...sentAlertsRequest(filters, now, periodsBack), page: 1, pageSize: 1 }),
        abortSignal,
      ),
    );
  };
}

/** Scheduled alerts still to send; expired ones are left out */
function injectScheduledAlerts(): MetricLoader {
  const alerts = inject(AlertsApi);
  return (_filters, _settings, abortSignal) =>
    totalOf(alerts.getScheduledAlerts(undefined, false, 1, 1), abortSignal).then((value) => ({ value }));
}

/** The facilities this user can see */
function injectFacilities(): MetricLoader {
  const facilities = inject(FacilitiesService);
  return (_filters, _settings, abortSignal) =>
    totalOf(facilities.facilitiesGetItems({ tenantId: ApiConfig.tenantId, page: 1, pageSize: 1 }), abortSignal).then(
      (value) => ({ value }),
    );
}

/** The count for the range, against the equally long window before it; `now` is pinned so the windows abut */
async function compareWithPrevious(count: (periodsBack: number) => Promise<number>): Promise<MetricReading> {
  const [current, previous] = await Promise.all([count(0), count(1)]);
  return { value: current, detail: changeFromPrevious(current, previous) };
}

export function changeFromPrevious(current: number, previous: number): MetricText {
  if (previous === 0) {
    return { key: current === 0 ? 'Dashboard.Metric.NoChange' : 'Dashboard.Metric.NonePreviously' };
  }

  const percent = Math.round(((current - previous) / previous) * 100);
  const change = percent > 0 ? `+${percent}%` : `${percent}%`;
  return { key: 'Dashboard.Metric.VsPrevious', params: { change } };
}
